import { Action } from '@wix/component-protocol/schema';
import {
  EditActionDefinition,
  NativeActionNames,
} from '@wix/editor-platform-host-integration-apis';
import { EditAction } from '../types';
import { EditActionsPresetsStore } from './editActionsPresetsStore';

/**
 * Factory function that creates a method decorator for automatically registering style presets.
 *
 * This decorator factory wraps the `buildContributedEditAction` method to intercept
 * its return value. If the returned action definition contains `stylePresets`,
 * they are automatically registered in the `EditActionsPresetsStore`.
 *
 * @returns A decorator function that handles both decorator formats:
 *   - TypeScript experimental: (target, propertyKey, descriptor) - used in tests
 *   - Babel Stage 2: (elementDescriptor) - used in dev server
 *
 * @remarks
 * - Dev server (yarn start): Babel with Stage 2 decorators
 * - Tests (yarn test:unit): ts-jest with TypeScript experimental decorators
 */
export function WithStylePresets() {
  const createWrapper = (originalMethod: any) =>
    function (
      this: any,
      actionName: NativeActionNames,
      actionDefinition: Action,
      hostDefinitionOrFactory: EditAction,
    ): EditActionDefinition<NativeActionNames> {
      const result = originalMethod.call(
        this,
        actionName,
        actionDefinition,
        hostDefinitionOrFactory,
      );

      if (result?.stylePresets) {
        EditActionsPresetsStore.setStylePresets(
          actionName,
          result.stylePresets,
        );
      }

      return result;
    };

  return (
    targetOrElementDescriptor: any,
    propertyKey?: string | symbol,
    descriptor?: PropertyDescriptor,
  ): any => {
    // TypeScript experimental decorators: 3 parameters (target, propertyKey, descriptor)
    if (descriptor && propertyKey !== undefined) {
      descriptor.value = createWrapper(descriptor.value);
      return descriptor;
    }

    // Babel Stage 2 decorators: 1 parameter (elementDescriptor with nested descriptor)
    const elementDescriptor = targetOrElementDescriptor;
    return {
      ...elementDescriptor,
      descriptor: {
        ...elementDescriptor.descriptor,
        value: createWrapper(elementDescriptor.descriptor.value),
      },
    };
  };
}
